import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./variables.css";
import "./Player.css";
import API_URL from "./config";
import useTeamTheme from "./useTeamTheme";
import ReleasePlayerModal from "./ReleasePlayerModal";

const Player = () => {
  const { playerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { fromPage = "/my-team" } = location.state || {};
  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);
  const [seasonStats, setSeasonStats] = useState(null);
  const [error, setError] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [myTeamId, setMyTeamId] = useState(null);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [teamRoster, setTeamRoster] = useState([]);
  const [lifecycle, setLifecycle] = useState(null);

  useTeamTheme(player?.team);

  // Convert height in inches to feet+inches
  const formatHeight = (h) => {
    if (h === null || h === undefined) return null;
    const n = Number(h);
    if (Number.isNaN(n)) return h;
    const feet = Math.floor(n / 12);
    const inches = n % 12;
    return `${feet}'${inches}"`;
  };

  // Format birthdate to M/D/YYYY
  const formatBirthdate = (date) => {
    if (!date) return null;
    if (typeof date === 'string') {
      // Handle ISO format (2004-04-14T00:00:00)
      if (date.includes('T') || date.includes('-')) {
        const dateObj = new Date(date);
        const month = dateObj.getMonth() + 1;
        const day = dateObj.getDate();
        const year = dateObj.getFullYear();
        return `${month}/${day}/${year}`;
      }
      // Already in M/D/YYYY format, return as-is
      return date;
    }
    return date;
  };

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        let url = `${API_URL}/api/players/${playerId}`;
        
        // Include save_id parameter if there's an active save
        if (activeSaveId) {
          url += `?save_id=${activeSaveId}`;
        }
        
        const res = await fetch(url);
        if (!res.ok) {
          const data = await res.json();
          setError(data.detail || "Failed to load player");
          return;
        }

        const data = await res.json();
        setPlayer(data.player || null);
        setStats(data.stats || null);

        if (activeSaveId) {
          const params = new URLSearchParams({ save_id: activeSaveId });

          // Try to scope by current season when available.
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          const userId = user._id;
          if (userId) {
            try {
              const saveRes = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
                headers: { "X-User-ID": userId },
              });
              if (saveRes.ok) {
                const saveData = await saveRes.json();
                const currentSeason = saveData?.save?.currentSeason;
                if (currentSeason != null) {
                  params.append("season", String(currentSeason));
                }
                // Store lifecycle data for phase-aware features
                if (saveData?.save?.currentPhase) {
                  setLifecycle({ currentPhase: saveData.save.currentPhase });
                }
                // Store the user's team ID - set this regardless
                const teamId = saveData?.save?.teamId;
                if (teamId) {
                  setMyTeamId(teamId);
                  
                  // Fetch team roster for the release modal
                  try {
                    const teamRes = await fetch(`${API_URL}/api/teams/${teamId}/roster?save_id=${activeSaveId}`, {
                      headers: { "X-User-ID": userId },
                    });
                    if (teamRes.ok) {
                      const teamData = await teamRes.json();
                      setTeamRoster(teamData.roster || []);
                    }
                  } catch (err) {
                    // Error fetching team roster
                  }
                }
              }
            } catch (err) {
              // Fall back to save-wide stats when season lookup fails.
            }
          }

          const seasonRes = await fetch(
            `${API_URL}/api/stats/player/${playerId}/season-summary?${params.toString()}`
          );
          if (seasonRes.ok) {
            const seasonData = await seasonRes.json();
            setSeasonStats(seasonData || null);
          } else {
            setSeasonStats(null);
          }
        } else {
          setSeasonStats(null);
        }
      } catch (err) {
        setError(err.message || "Server error");
      }
    };

    fetchPlayer();
  }, [playerId]);

  const handleReleasePlayerClick = () => {
    setShowReleaseModal(true);
  };

  const handleConfirmRelease = async () => {
    try {
      setReleasing(true);
      setError("");
      const activeSaveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      
      const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/release-player?player_id=${playerId}`, {
        method: "POST",
        headers: { "X-User-ID": user._id }
      });

      const data = await res.json();

      if (res.ok) {
        setShowReleaseModal(false);
        navigate(fromPage);
      } else {
        const errorMsg = data.detail || "Failed to release player";
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || "Server error";
      setError(errorMsg);
    } finally {
      setReleasing(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="player-wrapper">
        <div className="player-header">
          <h2>Player Details</h2>
          {player && (
            <></>
          )}
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            {player && (player.teamId === myTeamId || player.team_id === myTeamId || (player.team && fromPage === "/my-team")) && lifecycle?.currentPhase !== "playoffs" && (
              <button 
                onClick={handleReleasePlayerClick}
                disabled={releasing || teamRoster.length <= 10}
                title={teamRoster.length <= 10 ? "You cannot have less than 10 players on your team. Sign free agents to release more players." : ""}
                style={{
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  padding: "0.5rem 1rem",
                  borderRadius: "4px",
                  cursor: releasing || teamRoster.length <= 10 ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  opacity: (releasing || teamRoster.length <= 10) ? 0.6 : 1
                }}
              >
                {releasing ? "Releasing..." : "Release Player"}
              </button>
            )}
            <button 
              onClick={() => navigate(fromPage)} 
              className="player-back-link"
              style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", font: "inherit" }}
            >
              ← Back to Team
            </button>
          </div>
        </div>
        {error && <p className="player-error">{error}</p>}
        {!player && !error && <p className="player-loading">Loading...</p>}

        {player && (
          <div className="player-card">
            <h3>{player.name || player.displayName || "Unnamed Player"}</h3>

            {/* Bio & Measurements */}
            <div className="player-bio-grid">
              <div>
                {player.position && (
                  <div className="player-bio-item">
                    <strong>Position:</strong> {player.position}
                  </div>
                )}
                {player.number != null && (
                  <div className="player-bio-item">
                    <strong>Number:</strong> {player.number}
                  </div>
                )}
                {player.team && (
                  <div className="player-bio-item">
                    <strong>Team:</strong> {player.teamName || player.team}
                  </div>
                )}
                {player.birthdate && (
                  <div className="player-bio-item">
                    <strong>Birthdate:</strong> {formatBirthdate(player.birthdate)}
                  </div>
                )}
                {player.experience != null && (
                  <div className="player-bio-item">
                    <strong>Experience:</strong> {player.experience} yrs
                  </div>
                )}
                {player.contract && player.contract.length > 0 && (
                  <div className="player-bio-item">
                    <strong>Contract:</strong> {player.contract}
                  </div>
                )}
              </div>
              <div>
                {player.height != null && (
                  <div className="player-bio-item">
                    <strong>Height:</strong> {formatHeight(player.height)}
                  </div>
                )}
                {player.weight != null && (
                  <div className="player-bio-item">
                    <strong>Weight:</strong> {player.weight} lbs
                  </div>
                )}
                {player.stamina != null && (
                  <div className="player-bio-item">
                    <strong>Stamina:</strong> {player.stamina}
                  </div>
                )}
                {(player.injury != null || player.injuryType) && (
                  <>
                    <div className="player-bio-item">
                      <strong>Durability:</strong>{" "}
                      <span style={{
                        color: typeof player.injury === 'number' 
                          ? (player.injury >= 80 ? "#4ade80"
                            : player.injury >= 60 ? "#facc15"
                            : player.injury >= 40 ? "#fb923c"
                            : "#f87171")
                          : "#888888"
                      }}>
                        {typeof player.injury === 'number'
                          ? (player.injury >= 80  ? `${player.injury} — Durable`
                            : player.injury >= 60 ? `${player.injury} — Average`
                            : player.injury >= 40 ? `${player.injury} — Fragile`
                            : `${player.injury} — Glass`)
                          : "N/A"}
                      </span>
                    </div>
                    <div className="player-bio-item">
                      <strong>Health:</strong>{" "}
                      <span style={{
                        color: player.injuryType ? "#f87171" : "#4ade80"
                      }}>
                        {player.injuryType 
                          ? `Injured - ${player.injuryType} (${player.injuryGamesRemaining || 0} games)` 
                          : "Healthy"}
                      </span>
                    </div>
                  </>
                )}

              </div>
            </div>

            {/* Attributes */}
            {seasonStats && (
              <div className="player-season-stats">
                <h4>
                  Season Averages
                  {seasonStats.season != null ? ` (Season ${seasonStats.season})` : ""}
                </h4>
                <div className="player-season-grid">
                  <div className="player-season-item"><strong>GP</strong><span>{seasonStats.gamesPlayed || 0}</span></div>
                  <div className="player-season-item"><strong>PPG</strong><span>{seasonStats.stats?.points?.avg?.toFixed(1) || "0.0"}</span></div>
                  <div className="player-season-item"><strong>APG</strong><span>{seasonStats.stats?.assists?.avg?.toFixed(1) || "0.0"}</span></div>
                  <div className="player-season-item"><strong>RPG</strong><span>{seasonStats.stats?.rebounds?.avg?.toFixed(1) || "0.0"}</span></div>
                  <div className="player-season-item"><strong>SPG</strong><span>{seasonStats.stats?.steals?.avg?.toFixed(1) || "0.0"}</span></div>
                  <div className="player-season-item"><strong>BPG</strong><span>{seasonStats.stats?.blocks?.avg?.toFixed(1) || "0.0"}</span></div>
                  <div className="player-season-item"><strong>FG%</strong><span>{seasonStats.shootingPct?.fieldGoal?.toFixed(1) || "0.0"}%</span></div>
                  <div className="player-season-item"><strong>3P%</strong><span>{seasonStats.shootingPct?.threePointer?.toFixed(1) || "0.0"}%</span></div>
                  <div className="player-season-item"><strong>FT%</strong><span>{seasonStats.shootingPct?.freeThrow?.toFixed(1) || "0.0"}%</span></div>
                </div>
              </div>
            )}

            <div>
              <h4>Attributes</h4>
              <div className="player-attributes">
                {[
                  ["Close", player.close],
                  ["Mid", player.mid],
                  ["Three", player.three],
                  ["Free Throw", player.free_throw],
                  ["Offensive IQ", player.offensive_iq],
                  ["Defensive IQ", player.defensive_iq],
                  ["Speed", player.speed],
                  ["Strength", player.strength],
                  ["Jumping", player.jumping],
                  ["Dunk", player.dunk],
                  ["Passing", player.passing],
                  ["Dribbling", player.dribbling],
                  ["Rebounding", player.rebounding],
                ].map(([label, value]) =>
                  value != null ? (
                    <div key={label} className="player-attribute-row">
                      <span className="player-attribute-label">{label}</span>
                      <span className="player-attribute-value">{value}</span>
                    </div>
                  ) : null
                )}
              </div>
            </div>

            {/* Optional raw stats blob if provided */}
            {stats && (
              <div className="player-game-stats">
                <h4>Game Stats</h4>
                <ul>
                  {Object.entries(stats).map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong> {value}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Release Player Modal */}
      {showReleaseModal && player && (
        <ReleasePlayerModal
          player={player}
          roster={teamRoster}
          onClose={() => setShowReleaseModal(false)}
          onConfirm={handleConfirmRelease}
          isLoading={releasing}
        />
      )}
    </>
  );
};

export default Player;
